import { Link, NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/app-context';
import { HELP_ITEM, visibleSections, type NavItem } from '@/app/navigation';
import { UnreadBadge, useUnreadAlerts } from '@/features/alerts/Bell';
import { useAuth } from '@/features/auth/auth-context';
import { cn } from '@/lib/cn';
import { BrandMark } from './BrandMark';

function SidebarLink({ item, badge = 0 }: { item: NavItem; badge?: number }) {
  const { t } = useTranslation();
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      className={({ isActive }) =>
        cn(
          'flex h-11 items-center gap-3.5 rounded-control px-4 text-[16px] text-ink-2 transition-colors',
          'hover:bg-ground hover:text-ink',
          isActive &&
            'bg-primary-soft font-semibold text-primary hover:bg-primary-soft hover:text-primary',
        )
      }
    >
      <Icon className="size-5 shrink-0" strokeWidth={1.75} />
      <span className="truncate">{t(`nav.${item.key}`)}</span>
      <UnreadBadge count={badge} className="ml-auto" />
    </NavLink>
  );
}

export function Sidebar() {
  const { t } = useTranslation();
  const { canAny } = useAuth();
  const context = useAppContext();
  const sections = visibleSections(canAny);
  const unread = useUnreadAlerts();

  return (
    <aside className="sticky top-0 hidden h-dvh flex-col print:!hidden overflow-y-auto border-r border-line bg-sidebar px-4 py-6 lg:flex">
      <Link to="/" className="mb-6 flex items-center gap-3 rounded-control px-2">
        <BrandMark logoUrl={context?.company.logoUrl} />
        <span className="min-w-0">
          <span className="block text-[19px] leading-tight font-semibold text-ink">Light BM</span>
          <span className="block truncate text-sm text-muted">{context?.company.displayName}</span>
        </span>
      </Link>

      <nav aria-label={t('nav.label')} className="flex-1 space-y-5">
        {sections.map((section) => (
          <div key={section.key}>
            {section.key !== 'main' && (
              <p className="mb-1.5 px-4 text-sm font-medium text-muted">
                {t(`nav.sections.${section.key}`)}
              </p>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <li key={item.key}>
                  <SidebarLink item={item} badge={item.key === 'alerts' ? unread : 0} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="mt-6 border-t border-line pt-4">
        <SidebarLink item={HELP_ITEM} />
        <p className="mt-3 px-4 text-caption text-muted">
          {t('app.version', { version: context?.version ?? '' })}
        </p>
      </div>
    </aside>
  );
}
